/**
 * Authentication Service (Web)
 * Google OAuth authentication via local Easy Auth emulator or Azure Easy Auth
 */

import { API_CONFIG } from '../config/api';

const API_TIMEOUT_MS = 30000; // 30 second timeout (matching mobile app)

export interface AuthUser {
  id: string;
  googleId?: string;
  email?: string;
  emailVerified?: boolean;
  displayName?: string;
  givenName?: string;
  familyName?: string;
  picture?: string;
  locale?: string;
  provider?: 'google' | 'legacy';
  isNewUser?: boolean;
  registrationStatus?: string;
  registeredAt?: number;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
}

interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  expiresAt: number | null;
  approvalStatus: 'pending' | 'approved' | 'rejected' | null;
}

const AUTH_STORAGE_KEY = 'solidrange_auth';

class AuthService {
  private state: AuthState = {
    isAuthenticated: false,
    user: null,
    token: null,
    expiresAt: null,
    approvalStatus: null,
  };

  /**
   * Fetch with timeout wrapper (matching mobile app pattern)
   */
  private async fetchWithTimeout(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        credentials: 'include', // Important: include cookies for session auth
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please check your connection');
      }
      throw error;
    }
  }

  /**
   * Initialize authentication service
   */
  async initialize(): Promise<AuthState> {
    try {
      // Development mode - check for stored dev auth (don't auto-login anymore)
      if (process.env.REACT_APP_DEV_AUTH_ENABLED === 'true') {
        // Load stored dev auth if available
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        if (stored) {
          const parsed: AuthState = JSON.parse(stored);
          // Check if token is expired
          if (parsed.expiresAt && parsed.expiresAt > Date.now()) {
            this.state = parsed;
            console.log('[Auth] Restored dev auth session for:', this.state.user?.email);
            return this.state;
          }
        }
        // No valid session - user needs to select a dev user to login
        console.log('[Auth] Dev mode: No active session, login required');
        return this.state;
      }

      // Load auth state from localStorage
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (stored) {
        const parsed: AuthState = JSON.parse(stored);
        
        // Check if token is expired
        if (parsed.expiresAt && parsed.expiresAt > Date.now()) {
          this.state = parsed;
          
          // Verify authentication with backend (check /.auth/me)
          const verified = await this.verifyAuthentication();
          if (!verified) {
            // Session invalid, clear state
            await this.clearLocalState();
          }
        } else {
          // Token expired, clear state
          await this.clearLocalState();
        }
      }
    } catch (error) {
      await this.clearLocalState();
    }

    return this.state;
  }

  /**
   * Sign in with Google via redirect to Easy Auth
   * This is used when not using the Google Sign-In button component
   */
  async signInWithGoogle(): Promise<void> {
    try {
      // Store current URL for post-login redirect
      sessionStorage.setItem('auth_redirect_url', window.location.pathname);

      // Redirect to Easy Auth Google login endpoint (works both locally and in Azure)
      const callbackUrl = `${window.location.origin}/auth/callback`;
      const loginUrl = `${API_CONFIG.baseUrl}/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(callbackUrl)}`;
      window.location.href = loginUrl;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Sign in with Google ID token (from @react-oauth/google component)
   * Sends the token to Easy Auth emulator for validation and session creation
   */
  async signInWithIdToken(idToken: string): Promise<boolean> {
    try {
      console.log('[Auth] Sign in with ID token...');
      
      // Send ID token to Easy Auth endpoint for validation
      const response = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/.auth/login/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id_token: idToken }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Auth] Easy Auth login failed:', response.status, errorText);
        throw new Error(`Authentication failed: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Auth] Easy Auth login successful');

      // Store the token immediately so we can use it for authenticated requests
      const authToken = data.authenticationToken;

      // Now fetch user info from /.auth/me and register with backend
      const meResponse = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/.auth/me`, {
        method: 'GET',
        headers: {
          'X-ZUMO-AUTH': authToken,
        },
      });

      if (!meResponse.ok) {
        console.error('[Auth] Failed to fetch /.auth/me:', meResponse.status);
        throw new Error('Failed to fetch user info');
      }

      const meData = await meResponse.json();
      console.log('[Auth] /.auth/me response:', JSON.stringify(meData, null, 2));

      if (!meData || meData.length === 0) {
        console.error('[Auth] Empty /.auth/me response');
        throw new Error('No user info returned');
      }

      const authInfo = meData[0];
      const claims = authInfo.user_claims || [];

      console.log('[Auth] authInfo.user_id:', authInfo.user_id);

      // Extract user info from claims
      const getClaim = (type: string): string | undefined => {
        const claim = claims.find((c: any) => c.typ === type || c.typ.endsWith(type));
        // Filter out session IDs
        const val = claim?.val;
        if (val && val.startsWith('sid:')) {
          return undefined; // Ignore session IDs
        }
        return val;
      };

      // Azure Easy Auth v3: user_id field contains the actual Google user ID
      const userId = authInfo.user_id;
      
      if (!userId || userId.startsWith('sid:')) {
        console.error('[Auth] No valid user ID found in /.auth/me response!');
        console.error('[Auth] authInfo:', JSON.stringify(authInfo, null, 2));
        throw new Error('Invalid user ID');
      }

      console.log('[Auth] Using user_id from authInfo:', userId);

      const user: AuthUser = {
        id: userId,
        googleId: userId,
        email: getClaim('emailaddress') || getClaim('email') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'),
        emailVerified: getClaim('email_verified') === 'true',
        displayName: getClaim('name') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'),
        givenName: getClaim('givenname') || getClaim('given_name') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'),
        familyName: getClaim('surname') || getClaim('family_name') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'),
        picture: getClaim('picture'),
        provider: 'google',
      };

      console.log('[Auth] Extracted user:', JSON.stringify(user, null, 2));

      // Update auth state
      this.state = {
        isAuthenticated: true,
        user,
        token: authToken,
        expiresAt: authInfo.expires_on ? new Date(authInfo.expires_on).getTime() : Date.now() + (24 * 60 * 60 * 1000),
      };

      // Save to localStorage
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(this.state));

      // Register/update the user in the backend database
      // POST the user data extracted from /.auth/me
      try {
        console.log('[Auth] POSTing user data to /api/auth/user:', {
          userId: user.id,
          email: user.email,
          displayName: user.displayName,
          givenName: user.givenName,
          familyName: user.familyName,
          picture: user.picture,
          emailVerified: user.emailVerified,
        });

        const registerResponse = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/api/auth/user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-ZUMO-AUTH': authToken, // Include auth token to pass Azure Easy Auth
          },
          body: JSON.stringify({
            userId: user.id,
            email: user.email,
            displayName: user.displayName,
            givenName: user.givenName,
            familyName: user.familyName,
            picture: user.picture,
            emailVerified: user.emailVerified,
          }),
        });

        if (registerResponse.ok) {
          const userData = await registerResponse.json();
          console.log('[Auth] User registration successful:', userData);
          // Update local user with data from database
          if (userData.user) {
            this.state.user = {
              ...this.state.user,
              id: userData.user.id || this.state.user?.id,
              email: userData.user.email || this.state.user?.email,
              displayName: userData.user.displayName || this.state.user?.displayName,
              givenName: userData.user.givenName || this.state.user?.givenName,
              familyName: userData.user.familyName || this.state.user?.familyName,
              picture: userData.user.picture || this.state.user?.picture,
              approvalStatus: userData.user.approvalStatus,
              rejectionReason: userData.user.rejectionReason,
            };
            // Update approval status in state
            this.state.approvalStatus = userData.user.approvalStatus || 'pending';
            // Save updated state
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(this.state));
          }
        } else {
          console.error('[Auth] User registration failed:', registerResponse.status, await registerResponse.text());
        }
      } catch (registerError) {
        console.error('[Auth] User registration error:', registerError);
        // Continue even if registration fails - user is authenticated
      }

      return true;
    } catch (error) {
      console.error('[Auth] signInWithIdToken error:', error);
      return false;
    }
  }

  /**
   * Handle authentication callback from Azure Easy Auth redirect
   */
  async handleCallback(): Promise<boolean> {
    try {
      const token = this.getToken();
      const headers: Record<string, string> = {};
      
      // Include X-ZUMO-AUTH header if we have a token
      if (token) {
        headers['X-ZUMO-AUTH'] = token;
      }
      
      // Check /.auth/me to get user info
      const meResponse = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/.auth/me`, {
        method: 'GET',
        headers,
      });

      if (!meResponse.ok) {
        console.error('[Auth] Failed to fetch /.auth/me:', meResponse.status);
        return false;
      }

      const meData = await meResponse.json();
      console.log('[Auth] /.auth/me response:', JSON.stringify(meData, null, 2));

      if (!meData || meData.length === 0) {
        console.error('[Auth] Empty /.auth/me response');
        return false;
      }

      const authInfo = meData[0];
      const claims = authInfo.user_claims || [];

      console.log('[Auth] All claims:', JSON.stringify(claims, null, 2));
      console.log('[Auth] authInfo.user_id:', authInfo.user_id);
      console.log('[Auth] authInfo.provider_name:', authInfo.provider_name);

      // Extract user info from claims
      const getClaim = (type: string): string | undefined => {
        const claim = claims.find((c: any) => c.typ === type || c.typ.endsWith(type));
        // Filter out session IDs
        const val = claim?.val;
        if (val && val.startsWith('sid:')) {
          return undefined; // Ignore session IDs
        }
        return val;
      };

      // Azure Easy Auth v3: user_id field contains the actual Google user ID
      // The claims only contain session IDs (sid:xxx) which are useless
      const userId = authInfo.user_id;
      
      if (!userId || userId.startsWith('sid:')) {
        console.error('[Auth] No valid user ID found in /.auth/me response!');
        console.error('[Auth] authInfo:', JSON.stringify(authInfo, null, 2));
        return false;
      }

      console.log('[Auth] Using user_id from authInfo:', userId);

      const user: AuthUser = {
        id: userId,
        googleId: userId, // Use the actual Google user ID from authInfo.user_id
        email: getClaim('emailaddress') || getClaim('email') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'),
        emailVerified: getClaim('email_verified') === 'true',
        displayName: getClaim('name') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'),
        givenName: getClaim('givenname') || getClaim('given_name') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'),
        familyName: getClaim('surname') || getClaim('family_name') || getClaim('http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'),
        picture: getClaim('picture'),
        provider: 'google',
      };

      console.log('[Auth] Extracted user:', JSON.stringify(user, null, 2));

      // Store the token for authenticated requests
      const authToken = authInfo.access_token || 'session_auth';

      // Update auth state first (so makeAuthenticatedRequest can use the token)
      this.state = {
        isAuthenticated: true,
        user,
        token: authToken,
        expiresAt: authInfo.expires_on ? new Date(authInfo.expires_on).getTime() : Date.now() + (24 * 60 * 60 * 1000),
      };

      // Save to localStorage
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(this.state));

      // Register/update the user in the backend database
      // POST the claims extracted from /.auth/me to persist user data
      try {
        console.log('[Auth] POSTing user data to /api/auth/user:', {
          userId: user.id,
          email: user.email,
          displayName: user.displayName,
          givenName: user.givenName,
          familyName: user.familyName,
          picture: user.picture,
          emailVerified: user.emailVerified,
        });
        
        const registerResponse = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/api/auth/user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-ZUMO-AUTH': authToken, // Include auth token to pass Azure Easy Auth
          },
          body: JSON.stringify({
            userId: user.id,
            email: user.email,
            displayName: user.displayName,
            givenName: user.givenName,
            familyName: user.familyName,
            picture: user.picture,
            emailVerified: user.emailVerified,
          }),
        });

        if (registerResponse.ok) {
          const userData = await registerResponse.json();
          console.log('[Auth] User registration successful:', userData);
          // Update local user with data from database (more complete profile)
          if (userData.user) {
            this.state.user = {
              ...this.state.user,
              id: userData.user.id || this.state.user?.id,
              email: userData.user.email || this.state.user?.email,
              displayName: userData.user.displayName || this.state.user?.displayName,
              givenName: userData.user.givenName || this.state.user?.givenName,
              familyName: userData.user.familyName || this.state.user?.familyName,
              picture: userData.user.picture || this.state.user?.picture,
            };
            // Save updated state
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(this.state));
          }
        } else {
          console.error('[Auth] User registration failed:', registerResponse.status, await registerResponse.text());
        }
      } catch (registerError) {
        console.error('[Auth] User registration error:', registerError);
        // User registration/update failed, but authentication was successful
        // Continue with locally extracted claims
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Sign out
   */
  async signOut(): Promise<void> {
    try {
      // Clear local state
      await this.clearLocalState();
      
      // For token-based auth (X-ZUMO-AUTH), we just need to clear local state
      // No need to call API logout since we're not using cookies for auth
      window.location.href = '/login';
    } catch (error) {
      // Clear local state anyway and redirect to login
      await this.clearLocalState();
      window.location.href = '/login';
    }
  }

  /**
   * Clear local authentication state
   */
  private async clearLocalState(): Promise<void> {
    this.state = {
      isAuthenticated: false,
      user: null,
      token: null,
      expiresAt: null,
    };
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  /**
   * Verify authentication with backend by checking /.auth/me
   */
  async verifyAuthentication(): Promise<boolean> {
    try {
      const token = this.getToken();
      const headers: Record<string, string> = {};
      
      // Include X-ZUMO-AUTH header if we have a token
      if (token) {
        headers['X-ZUMO-AUTH'] = token;
      }
      
      const response = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/.auth/me`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      // /.auth/me returns an array; empty array means not authenticated
      return Array.isArray(data) && data.length > 0;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get current auth state
   */
  getState(): AuthState {
    return { ...this.state };
  }

  /**
   * Get current user
   */
  getUser(): AuthUser | null {
    return this.state.user;
  }

  /**
   * Check if authenticated
   */
  isAuthenticated(): boolean {
    return this.state.isAuthenticated;
  }

  /**
   * Get auth token for API requests (X-ZUMO-AUTH)
   */
  getToken(): string | null {
    return this.state.token;
  }

  /**
   * Make an authenticated API request with X-ZUMO-AUTH header
   * Required for cross-domain requests to Azure Easy Auth
   * In dev mode, also sends X-DEV-USER-EMAIL and X-DEV-USER-ID headers
   */
  async makeAuthenticatedRequest(url: string, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();
    
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };
    
    // Add X-ZUMO-AUTH header if we have a token
    if (token) {
      headers['X-ZUMO-AUTH'] = token;
    }

    // In dev mode, add custom headers so API knows which dev user is making the request
    if (process.env.REACT_APP_DEV_AUTH_ENABLED === 'true' && this.state.user) {
      headers['X-DEV-USER-EMAIL'] = this.state.user.email || '';
      headers['X-DEV-USER-ID'] = this.state.user.id || '';
    }

    return this.fetchWithTimeout(url, {
      ...options,
      headers,
    });
  }

  /**
   * Check approval status from backend
   */
  async checkApprovalStatus(): Promise<{ approvalStatus: 'pending' | 'approved' | 'rejected'; rejectionReason?: string } | null> {
    try {
      const token = this.getToken();
      if (!token) {
        console.warn('[Auth] No auth token, cannot check approval status');
        return null;
      }

      const response = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/api/auth/approval-status`, {
        method: 'GET',
        headers: {
          'X-ZUMO-AUTH': token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('[Auth] Approval status:', data.approvalStatus);
        
        // Update local state
        this.state.approvalStatus = data.approvalStatus;
        if (this.state.user) {
          this.state.user.approvalStatus = data.approvalStatus;
          this.state.user.rejectionReason = data.rejectionReason;
        }
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(this.state));
        
        return {
          approvalStatus: data.approvalStatus,
          rejectionReason: data.rejectionReason
        };
      }
      return null;
    } catch (error) {
      console.error('[Auth] Error checking approval status:', error);
      return null;
    }
  }

  /**
   * Get current approval status
   */
  getApprovalStatus(): 'pending' | 'approved' | 'rejected' | null {
    return this.state.approvalStatus;
  }

  /**
   * Check if user is approved
   */
  isApproved(): boolean {
    return this.state.approvalStatus === 'approved';
  }

  /**
   * Check if dev auth mode is enabled
   */
  isDevAuthEnabled(): boolean {
    return process.env.REACT_APP_DEV_AUTH_ENABLED === 'true';
  }

  /**
   * Development login - authenticate with a dev user email
   * Only works when REACT_APP_DEV_AUTH_ENABLED=true
   * 
   * Pre-defined users:
   * - dev@solidrange.local (admin, auto-approved)
   * - testuser1@solidrange.local (pending approval)
   * - testuser2@solidrange.local (pending approval)
   * - Any custom email (creates new pending user)
   */
  async devLogin(email: string): Promise<{ success: boolean; error?: string }> {
    if (process.env.REACT_APP_DEV_AUTH_ENABLED !== 'true') {
      return { success: false, error: 'Dev auth is not enabled' };
    }

    try {
      console.log('[Auth] Dev login for:', email);

      const response = await this.fetchWithTimeout(`${API_CONFIG.baseUrl}/api/auth/dev-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Auth] Dev login failed:', response.status, errorData);
        return { success: false, error: errorData.error || `Login failed: ${response.status}` };
      }

      const data = await response.json();
      console.log('[Auth] Dev login successful:', data.user?.email);

      // Update auth state
      this.state = {
        isAuthenticated: true,
        user: {
          id: data.user.id,
          googleId: data.user.id,
          email: data.user.email,
          emailVerified: true,
          displayName: data.user.displayName,
          givenName: data.user.givenName,
          familyName: data.user.familyName,
          picture: data.user.picture,
          provider: 'google',
          approvalStatus: data.user.approvalStatus,
          rejectionReason: data.user.rejectionReason,
        },
        token: data.authenticationToken,
        expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        approvalStatus: data.user.approvalStatus,
      };

      // Save to localStorage
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(this.state));

      return { success: true };
    } catch (error) {
      console.error('[Auth] Dev login error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }

  /**
   * Get list of pre-defined dev users
   */
  getDevUsers(): Array<{ email: string; displayName: string; description: string }> {
    return [
      { 
        email: 'dev@solidrange.local', 
        displayName: 'Dev Admin', 
        description: 'Admin user (auto-approved)' 
      },
      { 
        email: 'testuser1@solidrange.local', 
        displayName: 'Test User 1', 
        description: 'Regular user (pending approval)' 
      },
      { 
        email: 'testuser2@solidrange.local', 
        displayName: 'Test User 2', 
        description: 'Regular user (pending approval)' 
      },
    ];
  }
}

export const authService = new AuthService();
